import React, { useState, useRef, useEffect } from 'react';
import { getData, postData } from '@/services/api.service';
import { end_points } from '@/services/endpoints';
import { toast } from 'react-toastify';

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'support';
  timestamp: Date;
  attachments?: Array<{
    name: string;
    url: string;
    type: string;
  }>;
}

interface ChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Load previous chats when component opens
  useEffect(() => {
    if (isOpen) {
      fetchChats();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChats = async () => {
    try {
      setIsLoading(true);
      const response = await getData(end_points.chats.url);
      if (response?.status === 200 || response?.status === 201) {
        // Transform API response to ChatMessage format
        const chatMessages: ChatMessage[] = response?.data?.data?.map((chat: any) => ({
          id: chat.id?.toString() || Date.now().toString(),
          text: chat.message || '',
          sender: chat.sender === 'user' || chat.sender === 'customer' ? 'user' : 'support',
          timestamp: chat.created_at ? new Date(chat.created_at) : new Date(),
          attachments: chat.attach_file_name ? [{
            name: chat.attach_file_name,
            url: chat.attach_file_url || '',
            type: chat.attach_file_type || 'application/octet-stream',
          }] : undefined,
        })) || [];
        
        setMessages(chatMessages);
      }
    } catch (error) {
      console.error('Error fetching chats:', error);
      toast.error('Failed to load chat history');
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setAttachments((prev) => [...prev, ...files]);
    }
    // Reset input to allow selecting same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() && attachments.length === 0) return;
    if (isSending) return;

    const messageText = inputMessage.trim();
    const filesToSend = [...attachments];
    
    // Optimistically add user message to UI
    const tempMessageId = Date.now().toString();
    const newMessage: ChatMessage = {
      id: tempMessageId,
      text: messageText || '',
      sender: 'user',
      timestamp: new Date(),
      attachments: filesToSend.map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
        type: file.type,
      })),
    };

    setMessages((prev) => [...prev, newMessage]);
    setInputMessage('');
    setAttachments([]);
    setIsSending(true);
    setIsTyping(true);

    try {
      // Prepare FormData for file upload
      const formData = new FormData();
      formData.append('message', messageText);
      
      // Handle file attachments
      // If API expects the file object with parameter name 'attach_file_name'
      if (filesToSend.length > 0) {
        // Send the first file (or handle multiple files as needed)
        const file = filesToSend[0];
        formData.append('attach_file_name', file);
      } else {
        // Send empty string if no file
        formData.append('attach_file_name', '');
      }

      const response = await postData(end_points.sendMessage.url, formData);
      
      if (response?.status === 200 || response?.status === 201) {
        // Update the message with server response if needed
        if (response?.data?.data) {
          const serverMessage: ChatMessage = {
            id: response.data.data.id?.toString() || tempMessageId,
            text: response.data.data.message || messageText,
            sender: 'user',
            timestamp: response.data.data.created_at ? new Date(response.data.data.created_at) : new Date(),
            attachments: response.data.data.attach_file_name ? [{
              name: response.data.data.attach_file_name,
              url: response.data.data.attach_file_url || '',
              type: response.data.data.attach_file_type || 'application/octet-stream',
            }] : newMessage.attachments,
          };
          
          // Replace temp message with server response
          setMessages((prev) => prev.map(msg => 
            msg.id === tempMessageId ? serverMessage : msg
          ));
        }

        // Check if there's a support response in the API response
        if (response?.data?.support_message) {
          const supportMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            text: response.data.support_message.message || 'Thank you for your message. Our support team will get back to you shortly.',
            sender: 'support',
            timestamp: response.data.support_message.created_at ? new Date(response.data.support_message.created_at) : new Date(),
          };
          setMessages((prev) => [...prev, supportMessage]);
        } else {
          // If no immediate response, show typing indicator will be cleared
          // Support team will respond later, and it will be loaded on next fetchChats call
        }
        
        toast.success('Message sent successfully');
      } else {
        // Revert optimistic update on error
        setMessages((prev) => prev.filter(msg => msg.id !== tempMessageId));
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Revert optimistic update on error
      setMessages((prev) => prev.filter(msg => msg.id !== tempMessageId));
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="chat-window-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="chat-window-modal">
        {/* Header */}
        <div className="chat-window-header">
          <div className="chat-window-header-content">
            <div className="chat-window-header-info">
              <div className="chat-window-avatar">💬</div>
              <div>
                <h5 className="chat-window-title">Support Chat</h5>
                <span className="chat-window-status">Online</span>
              </div>
            </div>
            <button onClick={onClose} className="chat-window-close-btn">
              ×
            </button>
          </div>
        </div>

        {/* Messages Container */}
        <div ref={chatContainerRef} className="chat-window-messages-container">
          {isLoading && messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              Loading chat history...
            </div>
          )}
          {!isLoading && messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              No previous messages. Start a conversation!
            </div>
          )}
          {messages.map((message) => (
            <div
              key={message.id}
              className={`chat-window-message-wrapper ${message.sender === 'user' ? 'user-message' : 'support-message'}`}
            >
              <div className={`chat-window-message-bubble ${message.sender === 'user' ? 'user-message' : 'support-message'}`}>
                <div className="chat-window-message-text">{message.text}</div>
                {message.attachments && message.attachments.length > 0 && (
                  <div className="chat-window-attachments-container">
                    {message.attachments.map((attachment, index) => (
                      <div key={index} className="chat-window-attachment-item">
                        {attachment.type.startsWith('image/') ? (
                          <img
                            src={attachment.url}
                            alt={attachment.name}
                            className="chat-window-attachment-image"
                          />
                        ) : (
                          <div className="chat-window-attachment-file">
                            <span className="chat-window-attachment-icon">📎</span>
                            <span className="chat-window-attachment-name">{attachment.name}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="chat-window-timestamp">{formatTime(message.timestamp)}</div>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="chat-window-typing-indicator">
              <div className="chat-window-typing-dots">
                <span className="chat-window-typing-dot"></span>
                <span className="chat-window-typing-dot"></span>
                <span className="chat-window-typing-dot"></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Attachments Preview */}
        {attachments.length > 0 && (
          <div className="chat-window-attachments-preview">
            {attachments.map((file, index) => (
              <div key={index} className="chat-window-attachment-preview-item">
                {file.type.startsWith('image/') ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="chat-window-attachment-preview-image"
                  />
                ) : (
                  <div className="chat-window-attachment-preview-file">
                    <span>📎</span>
                    <span className="chat-window-attachment-preview-name">{file.name}</span>
                  </div>
                )}
                <button
                  onClick={() => removeAttachment(index)}
                  className="chat-window-remove-attachment-btn"
                >
                  ×
                </button>
                <div className="chat-window-attachment-preview-size">
                  {formatFileSize(file.size)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Input Area */}
        <div className="chat-window-input-container">
          <div className="chat-window-input-wrapper">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="chat-window-attach-btn"
              title="Attach file"
            >
              📎
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="chat-window-textarea"
              rows={1}
            />
            <button
              onClick={handleSendMessage}
              disabled={(!inputMessage.trim() && attachments.length === 0) || isSending}
              className="chat-window-send-btn"
            >
              {isSending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;

